<template>
  <div class="purchase-wrap">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="title-area" v-show="activeTab === 'plan'">
        <h2 class="page-title">选择适合您的版本 ✦</h2>
        <p class="desc">
          灵活的订阅方案，满足个人、团队及企业不同场景的法律AI需求。
        </p>
      </div>
      <div class="header-btn-group" v-show="activeTab === 'plan'">
        <a-button @click="activeTab = 'record'" class="record-btn"
          >购买记录</a-button
        >
        <!-- <a-button @click="activeTab = 'invoice'">发票管理</a-button> -->
      </div>
    </div>

    <!-- Tab切换容器 -->
    <div v-show="activeTab === 'plan'" class="plan-page">
      <!-- 顶部状态卡片行 -->
      <div class="status-row">
        <div
          v-for="statusItem in statusList"
          :key="statusItem.key"
          class="status-card"
        >
          <div class="status-icon">
            <!-- <component :is="statusItem.icon" /> -->
            <img :src="statusItem.icon" class="status-icon" />
          </div>
          <div class="status-text">
            <div class="status-label">{{ statusItem.label }}</div>
            <div class="status-value">{{ statusItem.value }}</div>
          </div>
        </div>
      </div>

      <!-- 三栏套餐卡片 -->
      <div class="plan-card-row">
        <PlanCard
          v-for="plan in planCardList"
          :key="plan.type"
          :plan-info="plan"
          @open-pay="handleOpenPay"
        />
      </div>

      <!-- 企业版模块 -->
      <EnterprisePlan :enterprise-data="enterpriseData" />
      <SafeServiceBar :service-list="safeServiceList" />
    </div>

    <!-- 购买记录页面 -->
    <div v-show="activeTab === 'record'">
      <OrderRecord
        :order-list="orderList"
        :year-range="yearRange"
        @update:year-range="
          yearRange = $event;
          fetchOrderRecords();
        "
        @search="
          searchKey = $event;
          fetchOrderRecords();
        "
        @backList="activeTab = 'plan'"
      />
    </div>

    <!-- 支付弹窗 -->
    <PayModal
      v-model:open="payModalVisible"
      :pay-info="currentPayInfo"
      @pay-success="handlePaySuccess"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import {
  getActiveActivities,
  getPurchaseRecords,
} from "../../api/purchase/recharge";
import PlanCard from "@/components/PurchasePlan/PlanCard.vue";
import EnterprisePlan from "@/components/PurchasePlan/EnterprisePlan.vue";
import SafeServiceBar from "@/components/PurchasePlan/SafeServiceBar.vue";
import PayModal from "@/components/PurchasePlan/PayModal.vue";
import OrderRecord from "@/components/PurchasePlan/OrderRecord.vue";

// Tab激活标识
const activeTab = ref("plan");
const expireTime = ref(localStorage.getItem("userExpireTime") || "");
// 顶部状态卡片数组
const statusList = computed(() => [
  {
    key: "current",
    icon: require("@/assets/vipImg/icon1.png"),
    label: "当前套餐",
    value: `企业版 · 于 ${expireTime.value} 到期`,
  },
  {
    key: "safe",
    icon: require("@/assets/vipImg/icon3.png"),
    label: "数据安全",
    value: "企业级加密保护",
  },
]);

// 三栏套餐卡片（从后端获取）
const planCardList = ref([]);

// 获取套餐列表
onMounted(async () => {
  try {
    const res = await getActiveActivities();
    if (res?.data) {
      planCardList.value = res.data;
    }
  } catch (err) {
    console.error("获取套餐列表失败", err);
  }
});

// 企业版数据对象
const enterpriseData = ref({
  title: "企业版",
  subTitle: "适合集团 / 机构客户",
  leftFeature: [
    "专属法律知识库",
    "私有化部署 / 专属实例",
    "API 对接与系统集成",
  ],
  midFeature: [
    "多层级权限与审计日志",
    "专属客户成功服务",
    "数据安全与合规保障",
  ],
  rightFeature: [
    "定制化功能开发",
    "合同模板库 / 风控知识库",
    "支持本地化部署与定制开发",
  ],
  customTip: "定制报价",
  customText: "专属方案与服务",
  btnText: "联系销售",
  btnColor: "#ff7d00",
});


// 底部安全服务栏数组
const safeServiceList = ref([
  {
    icon: require("@/assets/vipImg/icon5.png"),
    title: "数据安全可靠",
    desc: "企业级加密与权限保护",
  },
  {
    icon: require("@/assets/vipImg/icon6.png"),
    title: "合规稳定",
    desc: "符合数据安全与隐私要求",
  },
  {
    icon: require("@/assets/vipImg/icon7.png"),
    title: "灵活扩展",
    desc: "支持按需升级与功能扩展",
  },
  {
    icon: require("@/assets/vipImg/icon8.png"),
    title: "专业服务",
    desc: "7×24 小时技术支持",
  },
]);

// 购买记录
const orderList = ref([]);
const yearRange = ref(0); // 0=近1年, 1=一年, 2=两年, 3=三年
const searchKey = ref("");

// 获取购买记录
const fetchOrderRecords = async () => {
  try {
    const res = await getPurchaseRecords({
      searchKey: searchKey.value,
      yearRange: yearRange.value,
    });
    if (res?.isSuccess && res?.data) {
      orderList.value = res.data;
    }
  } catch (err) {
    console.error("获取购买记录失败", err);
  }
};

// 切换tab到购买记录时获取数据
watch(activeTab, (val) => {
  if (val === "record") {
    fetchOrderRecords();
  }
});

// 支付弹窗控制
const payModalVisible = ref(false);
const currentPayInfo = ref({});
// 打开支付弹窗
const handleOpenPay = (plan) => {
  currentPayInfo.value = plan;
  payModalVisible.value = true;
};

// 支付成功：通知 kevinHome 刷新 VIP 信息
const handlePaySuccess = () => {
  window.dispatchEvent(new CustomEvent("vip-paid"));
};
</script>

<style scoped lang="less">
:root {
  --primary: #1677ff;
  --purple: #9254de;
  --orange: #ff7d00;
  --gray-text: #666;
  --light-border: #e8ebf2;
  --card-bg: #fff;
  --bg-page: #f7f9fe;
  --radius-md: 12px;
  --radius-sm: 8px;
}
.purchase-wrap {
  padding: 32px;
  background: var(--bg-page);
  min-height: 100vh;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
  .page-title {
    font-size: 24px;
    margin: 0 0 6px;
    font-weight: 600;
  }
  .desc {
    color: var(--gray-text);
    margin: 0;
  }
  .header-btn-group {
    display: flex;
    gap: 12px;
    .record-btn {
      height: 36px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.88);
      box-shadow: 0px 8px 24px 0px rgba(31, 55, 95, 0.04);
      border: 1px solid #d8e3f5;
      color: #374a67;
    }
  }
}
// 顶部状态卡片
.status-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  .status-card {
    background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
    box-shadow: 0px 8px 24px 0px rgba(29, 54, 96, 0.05);
    border-radius: 18px 18px 18px 18px;
    border: 1px solid #dbe6f7;
    font-family: PingFangSC, PingFang SC;
    font-weight: 400;
    font-size: 17px;
    color: #142649;
    line-height: 23px;
    padding: 24px;
    display: flex;
    .status-icon {
      width: 42px;
      height: 42px;
    }
    .status-label {
      font-weight: 400;
      font-size: 13px;
      color: #7b8ba4;
      line-height: 16px;
    }
    // .status-value {
    //   font-size: 15px;
    //   font-weight: 500;
    // }
  }
}
// 三栏套餐卡片
.plan-card-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}
// Token加油包区域
.token-desc-row {
  display: grid;
  grid-template-columns: 68% 30%;
  gap: 2%;
  margin: 24px 0;
  .token-desc-card {
    background: var(--card-bg);
    border-radius: var(--radius-md);
    padding: 20px;
    border: 1px solid var(--light-border);
    .desc-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 500;
      margin-bottom: 10px;
    }
    .desc-list {
      margin: 0;
      padding-left: 18px;
      color: var(--gray-text);
      font-size: 13px;
      line-height: 1.6;
    }
  }
}
</style>
